import { NAMESPACE as UI_KIT_NAMESPACE, PdfViewer } from '@ui-kit'
import React, { useEffect, useRef, useState } from 'react'
import { FilePreviewProps } from '../../utils/types'
import { FILE_TYPE_TOASTER } from '@ui-kit'
import { EXTENSIONS, NAMESPACES } from '../../utils/constants'
import { pdfjs } from 'react-pdf'
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

const FilePreview: React.FC<FilePreviewProps> = ({
  url,
  fileType,
  setTotalPages,
  docxPreviewUrl,
}) => {
  const [textContent, setTextContent] = useState<string>('')
  const [isLoadingText, setIsLoadingText] = useState<boolean>(false)
  const pdfContainerRef = useRef<HTMLDivElement>(null)
  const [defaultZoomScale, setDefaultZoomScale] = useState<number | null>(null)

  useEffect(() => {
    let isCancelled = false

    const fetchTextContent = async () => {
      if (fileType === EXTENSIONS.TXT && url) {
        setIsLoadingText(true)
        setTextContent('')
        try {
          const response = await fetch(url)
          const text = await response.text()
          if (!isCancelled) {
            setTextContent(text)
          }
        } catch (error) {
          console.error('Error fetching text file:', error)
          if (!isCancelled) {
            setTextContent('Error loading text file')
          }
        } finally {
          if (!isCancelled) {
            setIsLoadingText(false)
          }
        }
      } else {
        setTextContent('')
        setIsLoadingText(false)
      }
    }

    fetchTextContent()

    return () => {
      isCancelled = true
    }
  }, [url, fileType])

  useEffect(() => {
    const container = pdfContainerRef.current

    if (!container || !url) {
      return
    }
    let page: PDFPageProxy | null = null
    let pdf: PDFDocumentProxy | null = null
    let cancelled = false
    const loadingTask = pdfjs.getDocument(url)
    const calculateScale = () => {
      if (cancelled || !page) {
        return
      }
      const containerWidth = container.clientWidth

      if (!containerWidth) {
        return
      }

      const pageWidth = page.getViewport({ scale: 1 }).width
      const scale = containerWidth / pageWidth

      setDefaultZoomScale(Math.min(Math.max(scale, 0.5), 3))
    }
    const loadPdf = async () => {
      try {
        pdf = await loadingTask.promise

        if (cancelled) {
          await pdf.destroy()
          pdf = null
          return
        }
        page = await pdf.getPage(1)
        if (cancelled) {
          await pdf.destroy()
          pdf = null
          return
        }

        calculateScale()
      } catch (error) {
        if (!cancelled) {
          setDefaultZoomScale(1)
          console.error('Unable to determine PDF scale', error)
        }
      }
    }

    loadPdf()

    const resizeObserver = new ResizeObserver(() => {
      calculateScale()
    })

    resizeObserver.observe(container)

    return () => {
      cancelled = true
      resizeObserver.disconnect()
      void loadingTask.destroy()
      if (pdf) {
        void pdf.destroy()
        pdf = null
      }
    }
  }, [url])

  if (!url) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-50">
        <p className="text-gray-500">
          {UI_KIT_NAMESPACE.SCAN_IN_PROGRESS_DOCUMENT_TOOLTIP}
        </p>
      </div>
    )
  }

  if (fileType === EXTENSIONS.PDF) {
    return (
      <div ref={pdfContainerRef} className="w-full">
        {defaultZoomScale && (
          <PdfViewer
            pdfUrl={url}
            height=""
            setTotalPages={setTotalPages}
            defaultZoomScale={defaultZoomScale}
          />
        )}
      </div>
    )
  }

  if (fileType === EXTENSIONS.IMAGE) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-50 overflow-auto">
        <img
          src={url}
          alt={NAMESPACES.PREVIEW}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    )
  }

  if (fileType === EXTENSIONS.DOCX || fileType === EXTENSIONS.DOC) {
    const previewUrl = docxPreviewUrl ?? url
    return (
      <div className="w-full" ref={pdfContainerRef}>
        {defaultZoomScale && (
          <PdfViewer
            pdfUrl={previewUrl}
            height=""
            setTotalPages={setTotalPages}
            defaultZoomScale={defaultZoomScale}
          />
        )}
      </div>
    )
  }

  if (fileType === EXTENSIONS.TXT) {
    return (
      <div className="flex w-full h-full bg-white overflow-auto">
        {isLoadingText ? (
          <p className="text-gray-500 p-4">{NAMESPACES.LOADING_PLACEHOLDER}</p>
        ) : (
          <pre
            className="font-mono text-sm text-gray-900 p-4 m-0 w-full"
            style={{ whiteSpace: 'pre' }}
          >
            {textContent}
          </pre>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center w-full h-full bg-gray-50">
      <p className="text-gray-500">
        {FILE_TYPE_TOASTER.FILE_TYPE_NOT_SUPPORTED}
      </p>
    </div>
  )
}

export default FilePreview
